import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, MoreHorizontal, Newspaper, Pencil, Plus, Search, Star, Trash2,
} from "lucide-react";

import { ArticleFormDialog } from "@/components/ArticleFormDialog";
import { useConfirm } from "@/components/confirm-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useArticles } from "@/hooks/useArticles";
import { useDeleteArticle } from "@/hooks/useArticleMutations";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { formatDate } from "@/lib/format";
import type { ArticleRow, WorkflowState } from "@/lib/types";
import { cn } from "@/lib/utils";

const STATES: { value: WorkflowState | ""; label: string }[] = [
  { value: "",                   label: "Any state" },
  { value: "published",          label: "Published" },
  { value: "private_only",       label: "Staff portal" },
  { value: "pending_review",     label: "Pending director" },
  { value: "pending_ceo_review", label: "Pending CEO" },
  { value: "owner_only",         label: "Only me" },
];

const PAGE_SIZE = 20;

/**
 * /articles — same shape as DocumentLibrary but for `Article`. Cover
 * image thumbnail in the leading cell, status badge keyed to the same
 * colour palette as the dashboard.
 */
export default function Articles() {
  const [search,      setSearch]      = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [status,      setStatus]      = useState<WorkflowState | "">("");
  const [scope,       setScope]       = useState<"my" | "all">("all");
  const [page,        setPage]        = useState(1);
  const [createOpen,  setCreateOpen]  = useState(false);
  const [editing,     setEditing]     = useState<ArticleRow | null>(null);

  const { data, isLoading, isFetching } = useArticles({
    search: search || undefined, status: status || undefined, scope, page,
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.count / PAGE_SIZE)) : 1;

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Articles</h1>
          <p className="text-sm text-muted-foreground">
            Editorial briefs, op-eds, and longer-form posts.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />New article
        </Button>
      </header>

      <ArticleFormDialog open={createOpen} onOpenChange={setCreateOpen} />
      <ArticleFormDialog
        open={editing !== null}
        onOpenChange={(o) => !o && setEditing(null)}
        article={editing}
      />

      <Card className="border-border/60 shadow-soft">
        <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center">
          <form
            className="relative flex-1"
            onSubmit={(e) => { e.preventDefault(); setSearch(searchDraft.trim()); setPage(1); }}
          >
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search title or body…"
              className="h-10 pl-9"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
            />
          </form>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={status}
              onChange={(e) => { setStatus(e.target.value as WorkflowState | ""); setPage(1); }}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm font-medium"
            >
              {STATES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <select
              value={scope}
              onChange={(e) => { setScope(e.target.value as "my" | "all"); setPage(1); }}
              className="h-10 rounded-md border border-input bg-background px-3 text-sm font-medium"
            >
              <option value="all">All visible</option>
              <option value="my">Mine only</option>
            </select>
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden border-border/60 shadow-soft">
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-muted/30">
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-5">Article</TableHead>
                <TableHead className="hidden lg:table-cell">Owner</TableHead>
                <TableHead className="hidden sm:table-cell">State</TableHead>
                <TableHead className="hidden md:table-cell">Views</TableHead>
                <TableHead className="hidden xl:table-cell">Updated</TableHead>
                <TableHead className="w-16 pr-5 text-right" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={6} className="py-4">
                      <Skeleton className="h-10 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : data && data.results.length > 0 ? (
                data.results.map((a) => (
                  <TableRow key={a.id} className="border-border/60">
                    <TableCell className="py-3 pl-5">
                      <div className="flex items-center gap-3 min-w-0">
                        {a.image_url ? (
                          <img
                            src={a.image_url}
                            alt=""
                            className="h-10 w-10 shrink-0 rounded-lg object-cover ring-1 ring-border"
                          />
                        ) : (
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-orange-50 text-orange-600">
                            <Newspaper className="h-4 w-4" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            {a.is_featured && (
                              <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                            )}
                            <Link
                              to={`/articles/${a.slug}`}
                              className="truncate text-sm font-medium hover:text-primary hover:underline"
                            >
                              {a.title}
                            </Link>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {a.category_display} · {a.reading_time} min read
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                      {a.owner_name || "—"}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <StateBadge state={a.status} label={a.status_display} />
                    </TableCell>
                    <TableCell className="hidden md:table-cell font-mono tabular-nums text-sm text-muted-foreground">
                      {a.view_count.toLocaleString()}
                    </TableCell>
                    <TableCell className="hidden xl:table-cell text-sm text-muted-foreground">
                      {formatDate(a.updated_at)}
                    </TableCell>
                    <TableCell className="pr-5 text-right">
                      <ArticleRowActions
                        article={a}
                        onEdit={() => setEditing(a)}
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="py-16">
                    <div className="text-center">
                      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                        <Newspaper className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <h3 className="text-sm font-semibold">No articles match</h3>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Try clearing a filter, or create the first one.
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Pagination
        page={page} totalPages={totalPages}
        count={data?.count} fetching={isFetching}
        hasPrev={!!data?.previous} hasNext={!!data?.next}
        onPrev={() => setPage((p) => Math.max(1, p - 1))}
        onNext={() => setPage((p) => p + 1)}
      />
    </div>
  );
}

function StateBadge({ state, label }: { state: WorkflowState; label: string }) {
  const className =
    state === "published"          ? "bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30"
  : state === "pending_review"     ? "bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-500/30"
  : state === "pending_ceo_review" ? "bg-violet-50 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-500/30"
  : state === "rejected"           ? "bg-rose-50 dark:bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-500/30"
  : state === "private_only"       ? "bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-500/30"
                                   : "bg-muted/40 text-foreground/80 border-border";
  return <Badge variant="outline" className={cn("border font-medium", className)}>{label}</Badge>;
}

function Pagination({
  page, totalPages, count, fetching, hasPrev, hasNext, onPrev, onNext,
}: {
  page: number; totalPages: number; count?: number; fetching: boolean;
  hasPrev: boolean; hasNext: boolean;
  onPrev: () => void; onNext: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="text-sm text-muted-foreground">
        {count != null ? (
          <>
            <span className="font-mono font-semibold tabular-nums">{count.toLocaleString()}</span> total
            {fetching && <span className="ml-2 text-xs">loading…</span>}
          </>
        ) : "—"}
      </div>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" disabled={!hasPrev} onClick={onPrev}>
          <ChevronLeft className="h-4 w-4" />Previous
        </Button>
        <div className="rounded-md border bg-card px-3 py-1.5 text-sm font-mono tabular-nums text-muted-foreground">
          {page} / {totalPages}
        </div>
        <Button variant="outline" size="sm" disabled={!hasNext} onClick={onNext}>
          Next<ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/** Per-row menu — Edit / Delete. Same permission shape as
 *  DocumentLibrary: Director+ or owner-if-not-yet-published. */
function ArticleRowActions({
  article, onEdit,
}: {
  article: ArticleRow;
  onEdit: () => void;
}) {
  const { data: user } = useCurrentUser();
  const del     = useDeleteArticle();
  const confirm = useConfirm();

  const canManage = !!user && (
    user.is_director_level
    || (article.owner_id === user.id && article.status !== "published")
  );

  if (!canManage) return null;

  async function onDelete() {
    const ok = await confirm({
      title: `Delete "${article.title}"?`,
      description: "This cannot be undone.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    del.mutate(article.slug);
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        <DropdownMenuItem onSelect={onEdit}>
          <Pencil className="mr-2 h-4 w-4" />Edit
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          disabled={del.isPending}
          onSelect={(e) => { e.preventDefault(); onDelete(); }}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          {del.isPending ? "Deleting…" : "Delete"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
